// Session lifecycle management implementation

import {
	createError,
	ErrorSeverity,
	ErrorType,
	errorHandler,
	MonologAppError,
} from "../error-handling/index.js";
import type { SessionManager } from "../interfaces/session-management.js";
import type { SummaryGenerator } from "../interfaces/summary-generation.js";
import type { IndexedDBWrapper } from "../storage/indexeddb-wrapper.js";
import type {
	ActivityEvent,
	Comment,
	Session,
	SessionSummary,
	TranscriptSegment,
	UserInteraction,
} from "../types/core.js";

export class SessionManagerImpl implements SessionManager {
	private activeSessions: Map<string, Session> = new Map();
	private storage: IndexedDBWrapper;
	private summaryGenerator: SummaryGenerator;

	constructor(storage: IndexedDBWrapper, summaryGenerator: SummaryGenerator) {
		this.storage = storage;
		this.summaryGenerator = summaryGenerator;
	}

	startSession(userId: string): Session {
		const sessionId = this.generateSessionId();
		const session: Session = {
			id: sessionId,
			userId,
			startTime: new Date(),
			transcript: [],
			comments: [],
			interactions: [],
			metrics: {
				totalDuration: 0,
				commentCount: 0,
				interactionCount: 0,
			},
			pausedIntervals: [],
		};

		this.activeSessions.set(userId, session);

		// Store session in IndexedDB immediately
		this.storage.saveSession(session).catch((error) => {
			errorHandler.handleError(
				createError(
					ErrorType.STORAGE,
					ErrorSeverity.MEDIUM,
					`Failed to save session to storage: ${error instanceof Error ? error.message : String(error)}`,
					error instanceof Error ? error : new Error(String(error)),
				),
			);
		});

		return session;
	}

	async endSession(
		sessionId: string,
		apiKey?: string,
		basicPolicy?: string,
	): Promise<SessionSummary> {
		const session = await this.getSessionById(sessionId);
		if (!session) {
			throw new MonologAppError(
				ErrorType.SESSION,
				ErrorSeverity.HIGH,
				`Session ${sessionId} not found`,
			);
		}

		// Ensure any active pause interval is closed
		if (session.pausedIntervals) {
			const lastInterval =
				session.pausedIntervals[session.pausedIntervals.length - 1];
			if (lastInterval && !lastInterval.end) {
				lastInterval.end = new Date();
			}
		}

		// Mark session as ended
		session.endTime = new Date();
		this.updateSessionMetrics(session);

		// Save updated session
		await this.storage.saveSession(session);

		// Remove from active sessions
		this.activeSessions.delete(session.userId);

		// Generate summary
		try {
			const summary = await this.generateSummary(session, apiKey, basicPolicy);

			// Update session title if generated
			if (summary.title) {
				session.title = summary.title;
				await this.storage.saveSession(session);
			}

			// Save summary to storage
			await this.storage.saveSummary(summary);

			return summary;
		} catch (error) {
			throw new MonologAppError(
				ErrorType.SESSION,
				ErrorSeverity.HIGH,
				`Failed to end session cleanly: ${error instanceof Error ? error.message : String(error)}`,
				error instanceof Error ? error : new Error(String(error)),
			);
		}
	}

	pauseSession(sessionId: string): void {
		const session = this.getActiveSessionById(sessionId);
		if (session) {
			if (!session.pausedIntervals) {
				session.pausedIntervals = [];
			}
			// Only start a new pause if the last one is closed
			const lastInterval =
				session.pausedIntervals[session.pausedIntervals.length - 1];
			if (!lastInterval || lastInterval.end) {
				session.pausedIntervals.push({ start: new Date() });
				this.updateSessionMetrics(session);
				this.storage.saveSession(session).catch(() => {});
			}
		}
	}

	resumeSession(sessionId: string): void {
		const session = this.getActiveSessionById(sessionId);
		if (session?.pausedIntervals) {
			const lastInterval =
				session.pausedIntervals[session.pausedIntervals.length - 1];
			if (lastInterval && !lastInterval.end) {
				lastInterval.end = new Date();
				this.updateSessionMetrics(session);
				this.storage.saveSession(session).catch(() => {});
			}
		}
	}

	async resumeStoredSession(sessionId: string): Promise<Session> {
		const session = await this.storage.getSession(sessionId);
		if (!session) {
			throw new MonologAppError(
				ErrorType.SESSION,
				ErrorSeverity.HIGH,
				`Session ${sessionId} not found in storage`,
			);
		}

		const resumeTime = new Date();
		if (session.endTime) {
			if (!session.pausedIntervals) {
				session.pausedIntervals = [];
			}
			// 停止中の期間を pause として保持し、再停止時の duration 計算から除外する
			session.pausedIntervals.push({
				start: new Date(session.endTime),
				end: resumeTime,
			});
		}

		// Clear endTime to make it active again
		session.endTime = undefined;
		this.updateSessionMetrics(session);

		// Add to active sessions
		this.activeSessions.set(session.userId, session);

		// Persist the change
		await this.storage.saveSession(session);

		return session;
	}

	trackActivity(sessionId: string, activity: ActivityEvent): void {
		const session = this.getActiveSessionById(sessionId);
		if (!session) {
			console.warn(`Cannot track activity for inactive session: ${sessionId}`);
			return;
		}

		// Update session based on activity type
		switch (activity.type) {
			case "speech":
				this.handleSpeechActivity(session, activity);
				break;
			case "comment":
				this.handleCommentActivity(session, activity);
				break;
			case "interaction":
				this.handleInteractionActivity(session, activity);
				break;
			case "silence":
				this.handleSilenceActivity(session, activity);
				break;
		}

		// Update metrics
		this.updateSessionMetrics(session);

		// Persist session changes
		this.storage.saveSession(session).catch((error) => {
			errorHandler.handleError(
				createError(
					ErrorType.STORAGE,
					ErrorSeverity.LOW,
					`Failed to save session activity: ${error instanceof Error ? error.message : String(error)}`,
					error instanceof Error ? error : new Error(String(error)),
				),
			);
		});
	}

	async generateSummary(
		session: Session,
		apiKey?: string,
		basicPolicy?: string,
	): Promise<SessionSummary> {
		return await this.summaryGenerator.createSummary(
			session,
			apiKey,
			basicPolicy,
		);
	}

	async getSummary(sessionId: string): Promise<SessionSummary | undefined> {
		return await this.storage.getSummary(sessionId);
	}

	updateTranscriptSegment(
		sessionId: string,
		segmentId: string,
		newText: string,
	): void {
		const session = this.getActiveSessionById(sessionId);
		if (session) {
			// Performance optimization: search from the end as most edits are on recent segments.
			// This avoids O(N) full array scan in the most common cases.
			let segment: TranscriptSegment | undefined;
			const transcript = session.transcript;
			for (let i = transcript.length - 1; i >= 0; i--) {
				if (transcript[i].id === segmentId) {
					segment = transcript[i];
					break;
				}
			}

			if (segment) {
				segment.text = newText;
				this.storage.saveSession(session).catch((error) => {
					errorHandler.handleError(
						createError(
							ErrorType.STORAGE,
							ErrorSeverity.LOW,
							`Failed to save session transcript update: ${error instanceof Error ? error.message : String(error)}`,
							error instanceof Error ? error : new Error(String(error)),
						),
					);
				});
			}
		}
	}

	async updateSessionTranscript(
		sessionId: string,
		transcript: TranscriptSegment[],
	): Promise<void> {
		// Update active session if it exists
		const activeSession = this.getActiveSessionById(sessionId);
		if (activeSession) {
			activeSession.transcript = transcript;
		}

		// Always update storage
		await this.storage.updateSessionTranscript(sessionId, transcript);
	}

	removeTranscriptSegment(sessionId: string, segmentId: string): void {
		const session = this.getActiveSessionById(sessionId);
		if (session) {
			// Search from the end and remove all matching IDs in place.
			// Reverse iteration avoids index shifting issues while keeping a single pass.
			const transcript = session.transcript;
			let removed = false;
			for (let i = transcript.length - 1; i >= 0; i--) {
				if (transcript[i].id === segmentId) {
					transcript.splice(i, 1);
					removed = true;
				}
			}

			if (removed) {
				this.storage.saveSession(session).catch((error) => {
					errorHandler.handleError(
						createError(
							ErrorType.STORAGE,
							ErrorSeverity.LOW,
							`Failed to save session transcript removal: ${error instanceof Error ? error.message : String(error)}`,
							error instanceof Error ? error : new Error(String(error)),
						),
					);
				});
			}
		}
	}

	getCurrentSession(userId: string): Session | null {
		return this.activeSessions.get(userId) || null;
	}

	// Additional helper methods

	async getSessionById(sessionId: string): Promise<Session | null> {
		// First check active sessions
		for (const session of this.activeSessions.values()) {
			if (session.id === sessionId) {
				return session;
			}
		}

		// Then check storage
		const storedSession = await this.storage.getSession(sessionId);
		return storedSession || null;
	}

	async getUserSessions(userId: string): Promise<Session[]> {
		return await this.storage.getSessionsByUser(userId);
	}

	addTranscriptSegment(sessionId: string, segment: TranscriptSegment): void {
		const session = this.getActiveSessionById(sessionId);
		if (session) {
			// Safeguard: ignore empty transcripts
			if (!segment.text || !segment.text.trim()) {
				return;
			}

			// Ensure segment has an ID
			if (!segment.id) {
				segment.id = `transcript-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
			}

			session.transcript.push(segment);
		}
	}

	addComment(sessionId: string, comment: Comment): void {
		const session = this.getActiveSessionById(sessionId);
		if (session) {
			session.comments.push(comment);
			session.metrics.commentCount++;
		}
	}

	addUserInteraction(sessionId: string, interaction: UserInteraction): void {
		const session = this.getActiveSessionById(sessionId);
		if (session) {
			session.interactions.push(interaction);
			session.metrics.interactionCount++;
		}
	}

	getEffectiveElapsedMs(sessionId: string, at: Date = new Date()): number {
		const session = this.getActiveSessionById(sessionId);
		if (!session) {
			return 0;
		}
		return this.calculateEffectiveElapsedMs(session, at);
	}

	private getActiveSessionById(sessionId: string): Session | null {
		for (const session of this.activeSessions.values()) {
			if (session.id === sessionId) {
				return session;
			}
		}
		return null;
	}

	private generateSessionId(): string {
		return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
	}

	private handleSpeechActivity(
		session: Session,
		activity: ActivityEvent,
	): void {
		// Add transcript segment if provided
		const data = activity.data;
		if (data && typeof data === "object" && "transcript" in data) {
			const speechData = data as {
				transcript: string;
				confidence?: number;
				isFinal?: boolean;
				id?: string;
			};

			// Safeguard: ignore empty transcripts
			if (!speechData.transcript || !speechData.transcript.trim()) {
				return;
			}

			const effectiveStart = this.calculateEffectiveElapsedMs(
				session,
				activity.timestamp,
			);
			const segment: TranscriptSegment = {
				id:
					speechData.id ||
					`transcript-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
				start: effectiveStart,
				end: effectiveStart + (activity.duration || 0),
				text: speechData.transcript,
				confidence: speechData.confidence || 1.0,
				isFinal: speechData.isFinal || true,
			};
			session.transcript.push(segment);
		}
	}

	private handleCommentActivity(
		session: Session,
		activity: ActivityEvent,
	): void {
		const data = activity.data;
		if (data && typeof data === "object" && "comment" in data) {
			const commentData = data as { comment: Comment };
			session.comments.push(commentData.comment);
			session.metrics.commentCount++;
		}
	}

	private handleInteractionActivity(
		session: Session,
		activity: ActivityEvent,
	): void {
		const data = activity.data;
		if (data && typeof data === "object" && "interaction" in data) {
			const interactionData = data as { interaction: UserInteraction };
			session.interactions.push(interactionData.interaction);
			session.metrics.interactionCount++;
		}
	}

	private handleSilenceActivity(
		_session: Session,
		_activity: ActivityEvent,
	): void {
		// Track silence periods for engagement analysis
		// This could be used for adaptive comment generation
	}

	private updateSessionMetrics(session: Session): void {
		const now = new Date();
		const end = session.endTime || now;
		let totalDuration = end.getTime() - session.startTime.getTime();

		// Subtract paused intervals
		if (session.pausedIntervals) {
			for (const interval of session.pausedIntervals) {
				const intervalStart = interval.start.getTime();
				const intervalEnd = (interval.end || now).getTime();
				totalDuration -= intervalEnd - intervalStart;
			}
		}

		session.metrics.totalDuration = Math.max(0, totalDuration);
	}

	private calculateEffectiveElapsedMs(session: Session, at: Date): number {
		let elapsed = at.getTime() - session.startTime.getTime();
		if (!session.pausedIntervals?.length) {
			return Math.max(0, elapsed);
		}

		for (const interval of session.pausedIntervals) {
			const intervalStart = interval.start.getTime();
			if (intervalStart >= at.getTime()) {
				continue;
			}
			const intervalEnd = Math.min(
				(interval.end || at).getTime(),
				at.getTime(),
			);
			elapsed -= Math.max(0, intervalEnd - intervalStart);
		}

		return Math.max(0, elapsed);
	}
}
